import { apiDelete, apiGet, apiPatch, apiUpload, type BodyOf, type ResponseOf } from './http';

export type Branding = ResponseOf<'/api/v1/branding/', 'get'>;

/**
 * The **reader's** brand when there is a session, and the **host's** when there is not.
 *
 * ⚠️ **The second half is why the route is still unauthenticated**: the sign-in page has to render a
 * brand before anybody has signed in, and for that visitor the backend resolves the host exactly as
 * it always did. ⚠️ **The first half is BUG-045** (backend, 2026-08-09): it answers
 * `request.user.tenant` once there is a cookie, because a white-label product naming another
 * organisation to somebody working in it is a trust defect — an Acme Admin signed in on a host mapped
 * to Globex read *"GLOBEX LLC"* in the sidebar while `/auth/me/` said *"Acme Inc"*. The response
 * carries `Vary: Cookie` for the same reason.
 *
 * ⚠️ **So this answer belongs to a session, and the cache entry must not outlive one.** The brand is
 * held at `staleTime: Infinity` by `TenantBrand` (above the router, so it does not unmount when the
 * reader changes), `TenantMark` and `AppShell`; `readerChanged` in `app/queryClient.ts` is what
 * empties and refetches it at every door out of a session — sign-in, invitation redemption,
 * sign-out and a 401 — and its docblock is where the mechanism is written down.
 *
 * **Branding is still not the boundary** (`.claude/rules/tenancy.md`): a display name and a mark,
 * never a source of access.
 */
export const fetchBranding = () => apiGet('/api/v1/branding/');

export type BrandingUpdate = BodyOf<'/api/v1/branding/', 'patch'>;

/** `BrandInfo` — what the brand looks like after a write; the same shape `GET` nests under `tenant`. */
export type Brand = ResponseOf<'/api/v1/branding/', 'patch'>;

/**
 * Set — or clear — the tenant accent (backend TASK-096). **`Admin` only**; the tenant comes from the
 * session and never from the host, so this writes the caller's own brand wherever they are looking.
 *
 * ⚠️ **A `422` here is a *measurement*, not a validation slip.** The server refuses an accent that
 * fails §2's contrast rule and says by how much (`page_ratio`, `foreground_ratio`, `threshold`), and
 * the screen refuses the same value before the request using the identical rule
 * (`theme/contrast.ts`) — a kindness, never the gate. The two are pinned to one fixture table whose
 * digest both suites assert; `theme/accentContrast.test.ts` is that end of it.
 *
 * `accent: null` clears it, which is how a tenant goes back to the product's own colour.
 */
export const updateBranding = (changes: BrandingUpdate) => apiPatch('/api/v1/branding/', changes);

/**
 * Replace the tenant logo — PNG or JPEG, at most 512 KB, **decided by the server from the bytes**.
 *
 * ⚠️ **And "from the bytes" is the whole container since BUG-061 (2026-08-09), not its first ones** —
 * so those two are not the whole of what can be refused: an image with anything appended after
 * `IEND` / `EOI` is a 422 as well. `apiUpload` in `api/http.ts` states what the walk proves and what
 * that costs; this line is deliberately not a second enumeration of the rule.
 *
 * A replaced logo deletes the previous file, so the URL changes on every upload; that is what makes
 * the year-long `immutable` cache on `/media/` correct rather than a way to serve a stale brand.
 */
export const uploadLogo = (file: File) => apiUpload('/api/v1/branding/logo/', file);

/** Remove the logo, so the product's own mark renders again — the counterpart of `accent: null`. */
export const deleteLogo = () => apiDelete('/api/v1/branding/logo/');
